<template>
  <Teleport to="body">
    <Transition
      enter-from-class="scale-95 opacity-0"
      enter-to-class="scale-100 opacity-100"
      leave-from-class="scale-100 opacity-100"
      leave-to-class="scale-95 opacity-0"
    >
      <div
        v-if="isShown"
        aria-hidden="true"
        :class="$style.card"
        class="z-popover origin-bottom-left rounded-lg border border-border bg-card px-4 py-3 text-foreground shadow-xl motion-safe:transition"
      >
        <div
          class="grid grid-cols-[minmax(0,1fr)_auto] gap-x-8 gap-y-2 text-sm"
        >
          <p
            class="col-span-2 -mx-4 -mb-2 border-b border-border px-4 pb-3 font-bold"
          >
            Keyboard shortcuts
          </p>

          <template v-for="[groupName, items] in groups" :key="groupName">
            <p
              class="col-span-2 mt-4 text-xs font-semibold text-muted-foreground"
            >
              {{ groupName }}
            </p>

            <template v-for="item in items" :key="item.id">
              <span class="text-left">{{ item.meta.name }}</span>
              <div class="whitespace-nowrap text-right font-mono">
                {{ item.formattedKeybind }}
              </div>
            </template>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, unref } from "vue";

import { groupBy } from "@/lib/collections";
import { createEventListener } from "@/lib/composables/event-listener";
import { useStablePending } from "@/lib/composables/stable-pending";
import { UNSTABLE_registeredBinds } from "@/lib/hotkeys";

const isCtrlHeld = ref(false);

const isShown = useStablePending(isCtrlHeld, {
  showDelay: 500,
  minShowTime: 200,
});

const groups = computed(() => {
  const collator = new Intl.Collator("en-US");
  const registered = UNSTABLE_registeredBinds.value
    .flatMap((collection) => unref(collection))
    .filter((keybind) => !unref(keybind.meta.hidden));

  registered.sort((a, b) => {
    return (
      collator.compare(a.meta.group ?? "", b.meta.group ?? "") ||
      collator.compare(a.meta.name, b.meta.name)
    );
  });

  return groupBy(registered, (keybind) => keybind.meta.group ?? "");
});

const PLATFORM = typeof navigator == "object" ? navigator.platform : "";
const IS_APPLE = /iP[aho]|Mac/.test(PLATFORM);

const onKey = (ev: KeyboardEvent) => {
  if (IS_APPLE) {
    isCtrlHeld.value =
      ev.metaKey &&
      !ev.shiftKey &&
      !ev.altKey &&
      !ev.ctrlKey &&
      ev.key === "Meta";
  } else {
    isCtrlHeld.value =
      ev.ctrlKey &&
      !ev.shiftKey &&
      !ev.altKey &&
      !ev.metaKey &&
      ev.key === "Control";
  }
};

// If the user tries to focus to another window, or switches tab while holding
// Ctrl/Meta, the keyup handler will not fire. I don't think there's a need for
// us to keep showing the list if the user isn't paying attention to the page,
// so we can safely hide the list.
const onBlur = () => {
  isCtrlHeld.value = false;
};

createEventListener(window, "keydown", onKey);
createEventListener(window, "keyup", onKey);
createEventListener(window, "blur", onBlur);
</script>

<style module>
.card {
  --boundary-padding: 1rem; /* 16px */
  position: fixed;
  bottom: var(--boundary-padding);
  left: var(--boundary-padding);
  max-width: calc(100dvw - var(--boundary-padding) * 2);
  max-height: calc(100dvh - var(--boundary-padding) * 2);
  overflow-y: auto;
}
</style>
